import { Body, Box, Vec2, World, type Contact } from 'planck';
import type {
  ArchivedChunk,
  BlockRecord,
  BlockView,
  GameState,
  MergeEvent,
  PlayerProgress,
  PrecisionEvent,
} from './types';
import { BLOCK_SIZE, PLAYFIELD_INSET, WORLD_WIDTH, chooseSpawnTier, tierValue } from './utils';

const PHYSICS_HZ = 120;
const PHYSICS_STEP = 1 / PHYSICS_HZ;
const GRAVITY = 15;
const FRICTION = 0.3;
const BASE_RESTITUTION = 0.3;
const RESTITUTION_VARIATION = 0.03;
const DENSITY = 1;
const DROP_COOLDOWN = 420;
const WORLD_REBASE_AT = -5_000;
const WORLD_REBASE_SHIFT = 4_000;
const PLAYFIELD_WIDTH = WORLD_WIDTH - PLAYFIELD_INSET * 2;
const HALF_BLOCK = BLOCK_SIZE / 2;
const WALL_HALF_WIDTH = 0.5;
const WALL_HALF_HEIGHT = 220;
const WALL_CENTER_Y = -WALL_HALF_HEIGHT + 0.5;
const MAX_MERGE_SPEED = 14;
const MAX_MERGE_SPIN = 10;

interface BlockData {
  kind: 'block';
  id: number;
  tier: number;
  restitution: number;
}

function defaultProgress(): PlayerProgress {
  return {
    score: '0',
    highestTier: 1,
    height: 0,
    combo: 0,
    milestones: [],
    precisionCount: 0,
    precisionChain: 0,
    longestPrecisionChain: 0,
  };
}

function physics(value: number): number {
  return value / BLOCK_SIZE;
}

function world(value: number): number {
  return value * BLOCK_SIZE;
}

function blockData(body: Body | null | undefined): BlockData | null {
  const data = body?.getUserData() as BlockData | undefined;
  return data?.kind === 'block' ? data : null;
}

function capVector(vector: Vec2, maximum: number): Vec2 {
  const length = vector.length();
  if (length > maximum) vector.mul(maximum / length);
  return vector;
}

export class GameEngine {
  readonly physicsHz = PHYSICS_HZ;
  readonly world = new World({ gravity: new Vec2(0, GRAVITY) });
  archivedChunks: ArchivedChunk[] = [];
  progress: PlayerProgress = defaultProgress();
  globalHeightOffset = 0;
  nextTier = 2;
  rngState = 0x6d2b79f5;
  onMerge?: (event: MergeEvent) => void;
  onPrecision?: (event: PrecisionEvent) => void;
  onMilestone?: (height: number) => void;

  private floor: Body;
  private leftWall: Body;
  private rightWall: Body;
  private archiveShelf: Body | null = null;
  private readonly blockBodies = new Map<number, Body>();
  private mergeQueue: Array<[number, number]> = [];
  private reservedBodies = new Set<number>();
  private lastDropAt = -Infinity;
  private lastDroppedId: number | null = null;
  private lastMergeAt = -Infinity;
  private chunkId = 1;
  private nextBodyId = 1;
  private physicsAccumulator = 0;

  constructor() {
    this.world.setAllowSleeping(true);
    this.world.setContinuousPhysics(true);
    this.floor = this.createFloor();
    this.leftWall = this.createWall(true);
    this.rightWall = this.createWall(false);
    this.world.on('begin-contact', (contact) => this.collectMerge(contact));
    this.nextTier = this.rollNextTier();
  }

  get blocks(): BlockView[] {
    return [...this.blockBodies.values()].map((body) => this.toView(body));
  }

  get canDrop(): boolean {
    return performance.now() - this.lastDropAt >= DROP_COOLDOWN;
  }

  step(deltaMs: number): void {
    this.physicsAccumulator += deltaMs / 1_000;
    while (this.physicsAccumulator + 1e-9 >= PHYSICS_STEP) {
      this.physicsAccumulator -= PHYSICS_STEP;
      this.world.step(PHYSICS_STEP, 8, 3);
      this.processMergeQueue();
    }
    this.updateProgressHeight();
  }

  dropAt(x: number, y: number): boolean {
    if (!this.canDrop) return false;
    const dropped = this.createBlock(this.nextTier, x, y);
    const data = blockData(dropped)!;
    this.lastDroppedId = data.id;
    this.nextTier = this.rollNextTier();
    this.lastDropAt = performance.now();
    return true;
  }

  reset(): void {
    this.clearBlocks();
    this.destroyArchiveShelf();
    this.archivedChunks = [];
    this.progress = defaultProgress();
    this.globalHeightOffset = 0;
    this.lastDroppedId = null;
    this.lastDropAt = -Infinity;
    this.lastMergeAt = -Infinity;
    this.mergeQueue = [];
    this.reservedBodies.clear();
    this.physicsAccumulator = 0;
    this.rngState = 0x6d2b79f5;
    this.nextBodyId = 1;
    this.chunkId = 1;
    this.restoreBoundaryPositions(0);
    this.nextTier = this.rollNextTier();
  }

  archiveDeepBodies(viewHeight: number): void {
    const highest = this.highestWorldY();
    const cutoff = highest + viewHeight * 2.6;
    const candidates = [...this.blockBodies.values()].filter((body) => {
      const data = blockData(body)!;
      const view = this.toView(body);
      const speed = body.getLinearVelocity().length();
      return view.bounds.max.y > cutoff
        && data.id !== this.lastDroppedId
        && (!body.isAwake() || speed < 0.04);
    });
    if (candidates.length < 12) return;
    const records = candidates.map((body) => this.toRecord(body, true));
    for (const body of candidates) this.destroyBlock(body);
    this.archivedChunks.push({ id: this.chunkId++, cutoffY: cutoff, blocks: records });
    this.destroyArchiveShelf();
    this.archiveShelf = this.createShelf(cutoff);
  }

  maybeRebase(cameraBottom: number): number {
    if (cameraBottom >= WORLD_REBASE_AT) return 0;
    this.world.shiftOrigin(new Vec2(0, physics(-WORLD_REBASE_SHIFT)));
    for (const chunk of this.archivedChunks) {
      chunk.cutoffY += WORLD_REBASE_SHIFT;
      for (const block of chunk.blocks) block.y += WORLD_REBASE_SHIFT;
    }
    this.globalHeightOffset += WORLD_REBASE_SHIFT;
    return WORLD_REBASE_SHIFT;
  }

  serializeState(): GameState {
    return {
      blocks: [...this.blockBodies.values()].map((body) => this.toRecord(body)),
      archivedChunks: structuredClone(this.archivedChunks),
      progress: structuredClone(this.progress),
      camera: {
        bottom: 28,
        targetBottom: 28,
        globalHeightOffset: this.globalHeightOffset,
      },
      nextTier: this.nextTier,
      rngState: this.rngState,
      physicsEngine: 'planck',
    };
  }

  restoreState(state: GameState): void {
    this.clearBlocks();
    this.destroyArchiveShelf();
    this.archivedChunks = structuredClone(state.archivedChunks);
    this.progress = { ...defaultProgress(), ...structuredClone(state.progress) };
    this.globalHeightOffset = state.camera.globalHeightOffset;
    this.restoreBoundaryPositions(this.globalHeightOffset);
    this.nextTier = state.nextTier;
    this.rngState = state.rngState;
    this.lastDroppedId = null;
    this.lastDropAt = -Infinity;
    this.mergeQueue = [];
    this.reservedBodies.clear();
    this.physicsAccumulator = 0;
    const legacyMatterState = state.physicsEngine !== 'planck';
    for (const record of state.blocks) {
      const restitution = legacyMatterState ? BASE_RESTITUTION : record.restitution;
      const body = this.createBlock(record.tier, record.x, record.y, record.id, restitution);
      body.setAngle(record.angle);
      body.setLinearVelocity(new Vec2(
        legacyMatterState ? record.velocityX * 60 / BLOCK_SIZE : record.velocityX,
        legacyMatterState ? record.velocityY * 60 / BLOCK_SIZE : record.velocityY,
      ));
      body.setAngularVelocity(legacyMatterState ? record.angularVelocity * 60 : record.angularVelocity);
      this.repairRestoredBounds(body);
      body.setAwake(true);
    }
    const lastChunk = this.archivedChunks[this.archivedChunks.length - 1];
    if (lastChunk) this.archiveShelf = this.createShelf(lastChunk.cutoffY);
  }

  highestWorldY(): number {
    const blocks = this.settledBlocks();
    return blocks.length ? Math.min(...blocks.map((body) => body.bounds.min.y)) : 0;
  }

  private settledBlocks(): BlockView[] {
    if (this.lastDroppedId === null) return this.blocks;
    const droppedBody = this.blockBodies.get(this.lastDroppedId);
    if (!droppedBody) {
      this.lastDroppedId = null;
      return this.blocks;
    }
    const velocity = droppedBody.getLinearVelocity();
    const hasSettled = performance.now() - this.lastDropAt > 650
      && (!droppedBody.isAwake()
        || (velocity.length() < 0.08 && Math.abs(droppedBody.getAngularVelocity()) < 0.08));
    if (hasSettled) {
      const dropped = this.toView(droppedBody);
      this.evaluatePrecision(dropped);
      this.lastDroppedId = null;
      return this.blocks;
    }
    return this.blocks.filter((body) => body.id !== this.lastDroppedId);
  }

  private evaluatePrecision(dropped: BlockView): void {
    const quarterTurn = Math.PI / 2;
    const normalized = ((dropped.angle % quarterTurn) + quarterTurn) % quarterTurn;
    const angleError = Math.min(normalized, quarterTurn - normalized);
    if (angleError > 0.045) {
      this.progress.precisionChain = 0;
      return;
    }
    const support = this.blocks.find((other) => {
      if (other.id === dropped.id) return false;
      const verticalGap = Math.abs(other.bounds.min.y - dropped.bounds.max.y);
      const horizontalOverlap = Math.min(other.bounds.max.x, dropped.bounds.max.x)
        - Math.max(other.bounds.min.x, dropped.bounds.min.x);
      return verticalGap <= 7 && horizontalOverlap >= BLOCK_SIZE * 0.62;
    });
    if (!support || Math.abs(support.position.x - dropped.position.x) > 5.5) {
      this.progress.precisionChain = 0;
      return;
    }
    this.progress.precisionChain += 1;
    this.progress.longestPrecisionChain = Math.max(
      this.progress.longestPrecisionChain,
      this.progress.precisionChain,
    );
    const multiplier = Math.min(this.progress.precisionChain, 5);
    const bonus = tierValue(dropped.tier) * BigInt(multiplier);
    const flow = this.progress.precisionChain >= 5
      ? 'ZEN'
      : this.progress.precisionChain >= 3
        ? 'FLOW'
        : this.progress.precisionChain === 2
          ? 'CALM'
          : 'PRECISION';
    this.progress.score = (BigInt(this.progress.score) + bonus).toString();
    this.progress.precisionCount += 1;
    this.onPrecision?.({
      x: dropped.position.x,
      y: dropped.position.y,
      tier: dropped.tier,
      bonus: bonus.toString(),
      chain: this.progress.precisionChain,
      flow,
    });
  }

  private repairRestoredBounds(body: Body): void {
    const rightEdge = WORLD_WIDTH - PLAYFIELD_INSET;
    const view = this.toView(body);
    let shiftX = 0;
    let shiftY = 0;
    if (view.bounds.min.x < PLAYFIELD_INSET) shiftX = PLAYFIELD_INSET - view.bounds.min.x + 0.5;
    if (view.bounds.max.x > rightEdge) shiftX = rightEdge - view.bounds.max.x - 0.5;
    if (view.bounds.max.y > 0) shiftY = -view.bounds.max.y - 0.5;
    if (shiftX || shiftY) {
      const position = body.getPosition();
      body.setPosition(new Vec2(position.x + physics(shiftX), position.y + physics(shiftY)));
    }
  }

  private createBlock(tier: number, x: number, y: number, id?: number, restitution?: number): Body {
    const blockId = id ?? this.nextBodyId++;
    this.nextBodyId = Math.max(this.nextBodyId, blockId + 1);
    const bounce = restitution ?? BASE_RESTITUTION
      + (this.random() * 2 - 1) * RESTITUTION_VARIATION;
    const data: BlockData = { kind: 'block', id: blockId, tier, restitution: bounce };
    const body = this.world.createDynamicBody({
      position: new Vec2(physics(x), physics(y)),
      angle: 0,
      linearVelocity: new Vec2(0, 0),
      angularVelocity: 0,
      allowSleep: true,
      awake: true,
      userData: data,
    });
    body.createFixture(new Box(0.5, 0.5), {
      density: DENSITY,
      friction: FRICTION,
      restitution: bounce,
    });
    this.blockBodies.set(blockId, body);
    this.progress.highestTier = Math.max(this.progress.highestTier, tier);
    return body;
  }

  private collectMerge(contact: Contact): void {
    const a = contact.getFixtureA().getBody();
    const b = contact.getFixtureB().getBody();
    const dataA = blockData(a);
    const dataB = blockData(b);
    if (!dataA || !dataB || dataA.tier !== dataB.tier) return;
    if (this.reservedBodies.has(dataA.id) || this.reservedBodies.has(dataB.id)) return;
    this.reservedBodies.add(dataA.id);
    this.reservedBodies.add(dataB.id);
    this.mergeQueue.push([dataA.id, dataB.id]);
  }

  private processMergeQueue(): void {
    const queue = this.mergeQueue.splice(0);
    for (const [idA, idB] of queue) {
      const a = this.blockBodies.get(idA);
      const b = this.blockBodies.get(idB);
      if (!a || !b) continue;
      const dataA = blockData(a)!;
      const tier = dataA.tier + 1;
      const positionA = a.getPosition();
      const positionB = b.getPosition();
      const velocityA = a.getLinearVelocity();
      const velocityB = b.getLinearVelocity();
      const position = new Vec2((positionA.x + positionB.x) / 2, (positionA.y + positionB.y) / 2);
      const velocity = capVector(new Vec2(velocityA.x + velocityB.x, velocityA.y + velocityB.y), MAX_MERGE_SPEED);
      const angle = a.getAngle() + b.getAngle();
      const angularSum = a.getAngularVelocity() + b.getAngularVelocity();
      const angularVelocity = Math.max(
        -MAX_MERGE_SPIN,
        Math.min(MAX_MERGE_SPIN, Math.sign(angularSum) * (
          Math.abs(a.getAngularVelocity()) + Math.abs(b.getAngularVelocity())
        )),
      );
      const trackedDrop = this.lastDroppedId === idA || this.lastDroppedId === idB;
      this.destroyBlock(a);
      this.destroyBlock(b);
      const merged = this.createBlock(tier, world(position.x), world(position.y));
      const mergedData = blockData(merged)!;
      merged.setAngle(angle);
      merged.setLinearVelocity(velocity);
      merged.setAngularVelocity(angularVelocity);
      if (trackedDrop) this.lastDroppedId = mergedData.id;
      const now = performance.now();
      this.progress.combo = now - this.lastMergeAt < 1_200 ? this.progress.combo + 1 : 1;
      this.lastMergeAt = now;
      this.progress.score = (BigInt(this.progress.score) + tierValue(tier)).toString();
      this.progress.highestTier = Math.max(this.progress.highestTier, tier);
      this.onMerge?.({ x: world(position.x), y: world(position.y), tier, combo: this.progress.combo });
    }
    this.reservedBodies.clear();
  }

  private updateProgressHeight(): void {
    const height = Math.max(0, Math.round((this.globalHeightOffset - this.highestWorldY()) / BLOCK_SIZE));
    if (height === this.progress.height) return;
    this.progress.height = height;
    const milestone = Math.floor(height / 10) * 10;
    if (milestone >= 10 && !this.progress.milestones.includes(milestone)) {
      this.progress.milestones.push(milestone);
      this.onMilestone?.(milestone);
    }
  }

  private rollNextTier(): number {
    return chooseSpawnTier(this.progress.highestTier, this.random());
  }

  private random(): number {
    let x = this.rngState | 0;
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    this.rngState = x >>> 0;
    return this.rngState / 0x1_0000_0000;
  }

  private toRecord(body: Body, archived = false): BlockRecord {
    const data = blockData(body)!;
    const position = body.getPosition();
    const velocity = body.getLinearVelocity();
    return {
      id: data.id,
      tier: data.tier,
      x: world(position.x),
      y: world(position.y),
      angle: body.getAngle(),
      velocityX: velocity.x,
      velocityY: velocity.y,
      angularVelocity: body.getAngularVelocity(),
      restitution: data.restitution,
      archived,
    };
  }

  private toView(body: Body): BlockView {
    const data = blockData(body)!;
    const position = body.getPosition();
    const velocity = body.getLinearVelocity();
    const x = world(position.x);
    const y = world(position.y);
    const angle = body.getAngle();
    const extent = HALF_BLOCK * (Math.abs(Math.cos(angle)) + Math.abs(Math.sin(angle)));
    return {
      id: data.id,
      tier: data.tier,
      position: { x, y },
      velocity: { x: velocity.x, y: velocity.y },
      angle,
      angularVelocity: body.getAngularVelocity(),
      restitution: data.restitution,
      speed: velocity.length(),
      isSleeping: !body.isAwake(),
      bounds: {
        min: { x: x - extent, y: y - extent },
        max: { x: x + extent, y: y + extent },
      },
    };
  }

  private createFloor(): Body {
    const body = this.world.createBody({
      position: new Vec2(physics(WORLD_WIDTH / 2), 0.5),
    });
    body.createFixture(new Box(physics(PLAYFIELD_WIDTH) / 2, 0.5), {
      friction: FRICTION,
      restitution: BASE_RESTITUTION,
    });
    return body;
  }

  private createWall(left: boolean): Body {
    const edge = physics(left ? PLAYFIELD_INSET : WORLD_WIDTH - PLAYFIELD_INSET);
    const x = left ? edge - WALL_HALF_WIDTH : edge + WALL_HALF_WIDTH;
    const body = this.world.createBody({ position: new Vec2(x, WALL_CENTER_Y) });
    body.createFixture(new Box(WALL_HALF_WIDTH, WALL_HALF_HEIGHT), {
      friction: FRICTION,
      restitution: BASE_RESTITUTION,
    });
    return body;
  }

  private createShelf(cutoff: number): Body {
    const halfHeight = physics(5);
    const body = this.world.createBody({
      position: new Vec2(physics(WORLD_WIDTH / 2), physics(cutoff) + halfHeight),
    });
    body.createFixture(new Box(physics(PLAYFIELD_WIDTH) / 2, halfHeight), {
      friction: FRICTION,
      restitution: BASE_RESTITUTION,
    });
    return body;
  }

  private destroyBlock(body: Body): void {
    const data = blockData(body);
    if (data) this.blockBodies.delete(data.id);
    this.world.destroyBody(body);
  }

  private clearBlocks(): void {
    for (const body of [...this.blockBodies.values()]) this.destroyBlock(body);
  }

  private destroyArchiveShelf(): void {
    if (!this.archiveShelf) return;
    this.world.destroyBody(this.archiveShelf);
    this.archiveShelf = null;
  }

  private restoreBoundaryPositions(offset: number): void {
    const offsetY = physics(offset);
    this.floor.setTransform(new Vec2(physics(WORLD_WIDTH / 2), offsetY + 0.5), 0);
    this.leftWall.setTransform(
      new Vec2(physics(PLAYFIELD_INSET) - WALL_HALF_WIDTH, WALL_CENTER_Y + offsetY),
      0,
    );
    this.rightWall.setTransform(
      new Vec2(physics(WORLD_WIDTH - PLAYFIELD_INSET) + WALL_HALF_WIDTH, WALL_CENTER_Y + offsetY),
      0,
    );
  }
}
